import React, { useState } from 'react';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import {
  type FragmentType,
  getFragmentData,
  makeFragmentData,
} from '@packages/gql/generated/shopping';
import type { WishlistLineItemsFragmentFragment as WishlistLineItemsType } from '@packages/gql/generated/shopping/graphql';
import { WishlistLineItemsFragmentFragmentDoc } from '@packages/gql/generated/shopping/WishlistLineItemsFragmentFragmentDoc';
import type { GlycerinWishListProduct } from '@packages/tracking/src/types/types';
import type { BasketActionsFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasketActionsFragmentFragmentDoc';
import { MiniDVLayer } from '@packages/modules/src/MiniDVLayer/MiniDVLayer.client';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { Link } from '@packages/modules/src/Link/Link';
import { ProductPowerSupplyFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductPowerSupplyFragmentFragmentDoc';
import { ProductPowerSupply } from '@packages/modules/src/ProductPowerSupply';
import { AvailabilityInfo } from '@packages/modules/src/AvailabilityInfo/AvailabilityInfo';
import { buildGlycerinWishlistProduct } from '@packages/modules/src/helpers/buildGlycerinObject/buildGlycerinObject';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { GTMEventGlycerinProductClick } from '@packages/tracking/src/types/events';
import { MoreMenu } from '../MoreMenu/MoreMenu';
import { ActionBar } from '../ActionBar/ActionBar';
import { PowerEfficiency } from './PowerEfficiency';
import { inlineButtonStyleTailwind } from '../Actions/Actions.styles';
import { BasketAction } from '../Actions/AddToBasketAction';
import { EditAction } from '../Actions/EditAction';
import { DeleteAction } from '../Actions/DeleteAction';
import { SimilarProductAction } from '../Actions/SimilarProductsAction';
import { LineItemPrice } from './LineItemPrice';

interface WishlistItemsProps {
  data?: FragmentType<typeof WishlistLineItemsFragmentFragmentDoc>;
}
/* GraphQL */ `
  fragment WishlistLineItemsFragment on WishlistLineItem {
    ...BasketActionsFragment
    sku
    akl
    imageUrl
    name
    isSoldOut
    availability {
      message
      state
    }
    price {
      amount
      currency
      isStartPrice
      saving {
        type
        value
      }
      unit
      uvp
      value
      valueOld
      valueUnit
    }
    unspecified
    variation
    powerEfficiency {
      text
      iconText
      level
      link
      icon
      dataSheet
    }
    powerSupply {
      chargerType
      maxPower
      minPower
      powerSupplyIncluded
    }
    extra {
      brand
    }
    ...SimilarProductsActionFragment
  }
`;

type LineItemActionsProps = {
  data: FragmentType<typeof BasketActionsFragmentFragmentDoc>;
  sku: WishlistLineItemsType['sku'];
  glycerinProduct: GlycerinWishListProduct;
  akl: WishlistLineItemsType['akl'];
};
const LineItemActions: React.FC<LineItemActionsProps> = ({ data, sku, glycerinProduct, akl }) => {
  const { isMobile } = useDeviceType();
  const [isMiniDVOpen, setIsMiniDVOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const children = [
    <BasketAction
      data={data}
      className="hidden w-auto md:flex"
      key="wishlistAddToBasket"
      glycerinProduct={glycerinProduct}
      onClick={() => setIsMoreMenuOpen(false)}
    />,
    <EditAction
      mode="wishlist"
      key="wishlistEdit"
      onClick={() => {
        setIsMiniDVOpen(true);
        setIsMoreMenuOpen(false);
      }}
    />,
    <DeleteAction
      mode="wishlist"
      key="wishlistDelete"
      lineItemSku={sku}
      glycerinProduct={glycerinProduct}
      onClick={() => setIsMoreMenuOpen(false)}
    />,
  ];

  const Wrapper = isMobile ? MoreMenu : ActionBar;

  return (
    <>
      <Wrapper isOpen={isMoreMenuOpen} onOpenChange={setIsMoreMenuOpen}>
        {isMobile ? children : children.reverse()}
      </Wrapper>
      <MiniDVLayer
        sku={sku}
        akl={akl}
        quantity={1}
        open={isMiniDVOpen}
        onClose={() => setIsMiniDVOpen(false)}
        mode="EIW"
      />
    </>
  );
};

export const WishlistLineItem: React.FC<WishlistItemsProps> = ({ data }) => {
  const fragmentData = getFragmentData(WishlistLineItemsFragmentFragmentDoc, data);

  const { language, localizeUrl } = useI18n();
  const dispatchGTMEvent = useTracking();

  if (!fragmentData) return null;

  const {
    imageUrl,
    name,
    availability,
    price,
    sku,
    akl,
    isSoldOut,
    variation,
    powerEfficiency,
    powerSupply,
  } = fragmentData;
  const glycerinProduct = buildGlycerinWishlistProduct(fragmentData);
  const productLinkParams = new URLSearchParams({ sku }).toString();

  const handleProductClick = () => {
    dispatchGTMEvent<GTMEventGlycerinProductClick>({
      event: 'ProductClick',
      ProductClickData: {
        category: 'Wishlist',
        productId: akl,
        articleId: glycerinProduct.articleId,
        skuId: sku,
      },
    });
  };

  let powerSupplyFragment;
  if (powerSupply) {
    const tmp = { ...powerSupply, __typename: 'PowerSupply' as const };
    powerSupplyFragment = makeFragmentData(tmp, ProductPowerSupplyFragmentFragmentDoc);
  }

  return (
    <div className="shadow-color-grey-dark bg-color-background-base relative mb-1.5 w-full overflow-visible rounded-[3px] shadow-xs md:mb-2">
      <div className="hyphen-auto grid max-w-screen-xl grid-cols-[5rem_3fr_1fr] gap-x-1.5 gap-y-1 px-1.5 pt-1.5 break-words md:grid-cols-[7.5rem_3fr_1fr]">
        <div className="relative z-10 col-start-1 row-start-1 row-end-6 flex size-[80px] translate-x-0 scale-100 transform flex-col items-center justify-center md:my-2 md:size-[120px]">
          <Link
            href={localizeUrl(`/p/${akl}?${productLinkParams}`, language)}
            onClick={handleProductClick}
          >
            <Image priority unoptimized alt={name} src={imageUrl} fill className="object-contain" />
          </Link>
        </div>
        <h5 className="text-size-body-sm md:text-size-headline-base col-span-2 col-start-2 row-span-1 mr-[28px] font-semibold md:col-span-1 md:mr-0">
          {name}
        </h5>
        <p className="text-size-body-sm col-span-2 col-start-2 row-start-2 mr-[28px] overflow-hidden text-ellipsis whitespace-nowrap md:col-end-3 md:mr-0">
          {variation}
        </p>
        <div className="col-span-2 col-start-2">
          <AvailabilityInfo
            availabilityMessage={availability.message}
            availabilityState={availability.state}
          />
        </div>
        {powerEfficiency && (
          <div className="col-span-2 col-start-2 row-start-4 row-end-5 md:row-start-4">
            <PowerEfficiency efficiency={powerEfficiency} />
          </div>
        )}
        {powerSupplyFragment && (
          <div className="col-start-2 row-start-5 row-end-6 mb-0.5 md:row-start-5">
            <ProductPowerSupply maskedData={powerSupplyFragment} showPowerSupplyMessage />
          </div>
        )}
        {!isSoldOut && (
          <div className="col-span-2 col-start-2 row-start-6 row-end-7 justify-self-end pt-1 md:col-start-3 md:row-span-3 md:row-start-1 md:pt-0">
            <LineItemPrice price={price} />
          </div>
        )}
        <BasketAction
          className={inlineButtonStyleTailwind}
          data={fragmentData}
          glycerinProduct={glycerinProduct}
        />
        {isSoldOut && (
          <SimilarProductAction
            data={fragmentData}
            className="col-span-3 col-start-1 row-span-1 row-start-6 mt-1 mb-2 md:col-span-1 md:col-start-2 md:row-auto md:mt-0 md:max-w-[50%]"
          />
        )}
      </div>
      <LineItemActions data={fragmentData} sku={sku} akl={akl} glycerinProduct={glycerinProduct} />
    </div>
  );
};
